/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    bool, string, object, array,
} from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import CheckIcon from '@material-ui/icons/Check';

import * as recipientActions from '../../../../../../../state/ducks/Recipient/Recipient-Actions';
import * as commonSelectors from '../../../../../../../state/ducks/Common/Common-Selectors';
import { getFeatureFlags } from '../../../../../../../state/ducks/Checkout/ducks/App/App-Selectors';

// pass a prop into breadcrumb that signals that a subheading is active
const styles = (theme) => ({
    breadcrumbRow: {
        height: '64px',
    },
    inactive: {
        color: theme.palette.colorNeutral60,
        padding: '20px 30px 19px 15px',
        display: 'inline-block',
        borderBottom: '3px solid transparent',
        fontSize: '16px',
        fontWeight: '600',
        cursor: 'pointer',
        '& span': {
            verticalAlign: 'top',
            display: 'inline-block',
        },
        [theme?.breakpoints?.down('md')]: {
            fontSize: '15px',
        },
    },
    active: {
        padding: '20px 30px 19px 15px',
        display: 'inline-block',
        color: theme.palette.colorNeutral80,
        fontSize: '16px',
        fontWeight: '600',
        borderBottom: `3px solid ${theme.palette.cms?.primary || theme.palette.colorPrimary}`,
        cursor: 'pointer',
        '& span': {
            verticalAlign: 'top',
            display: 'inline-block',
        },
        [theme?.breakpoints?.down('md')]: {
            fontSize: '15px',
        },
    },
    prefixIcon: {
        width: '23px',
        height: '23px',
        lineHeight: '16px',
        fontSize: '16px',
        margin: '0 8px 0 10px',
        borderRadius: '50%',
        padding: '2px 6px 5px 2px',
        display: 'inline-block',
        backgroundColor: theme.palette.primaryButton,
    },
    filledCheckMark: {
        color: theme.palette.white,
        fontSize: '18px !important',
    },
    prefixNumber: {
        backgroundColor: theme.palette.cms?.primary || theme.palette.colorPrimary,
        color: theme.palette.white,
        width: '24px',
        height: '24px',
        lineHeight: '13px',
        fontSize: '16px',
        margin: '0 8px 0 10px',
        borderRadius: '50%',
        padding: '6px',
        display: 'inline-block',
    },
});

const Breadcrumb = ({
    classes, active, orderId, featureFlags,
    continueToPayment, continueToReview,
    orderItemHasError,
}) => {
    const getPrefix = (index, isIcon) => (
        <span className={isIcon ? classes.prefixIcon : classes.prefixNumber}>
            {isIcon ? <CheckIcon className={classes.filledCheckMark} /> : index + 1}
        </span>
    );

    const isSinglePaymentLayoutEnabled = featureFlags['is-single-payment-layout-enabled'];
    const continueToReviewPage =  !(orderItemHasError?.length > 0) && continueToReview;
    const continueToPaymentPage =  !(orderItemHasError?.length > 0) && continueToPayment;

    return (
        <Grid container className={classes.breadcrumbRow}>
            <Grid item xs={isSinglePaymentLayoutEnabled ? 6 : 4} align="center">
                <Link
                    className={active === 'shipping' ? classes.active : classes.inactive}
                    to={(continueToPaymentPage ? `/checkout/shipping/${orderId}` : '#')}
                    data-test="checkout-breadcrumb-shipping"
                >
                    {getPrefix(0, continueToPaymentPage)}
                    <span>Delivery</span>
                </Link>
            </Grid>
            <Grid item xs={isSinglePaymentLayoutEnabled ? 6 : 4} align="center">
                <Link
                    className={active === 'payment' ? classes.active : classes.inactive}
                    to={(continueToPaymentPage || continueToReviewPage ? `/checkout/payment/${orderId}` : '#')}
                >
                    {getPrefix(1, continueToReviewPage)}
                    <span>Payment</span>
                </Link>
            </Grid>
            {
                !isSinglePaymentLayoutEnabled
                && (
                    <Grid item xs={4} align="center">
                        <Link
                            className={active === 'review' ? classes.active : classes.inactive}
                            to={(continueToReviewPage ? `/checkout/review-order/${orderId}` : '#')}
                        >
                            {getPrefix(2, continueToReviewPage)}
                            <span>Review</span>
                        </Link>
                    </Grid>
                )
            }
        </Grid>
    );
};

Breadcrumb.propTypes = {
    classes: object.isRequired,
    active: string.isRequired,
    orderId: string.isRequired,
    continueToPayment: bool.isRequired,
    continueToReview: bool.isRequired,
    featureFlags: object.isRequired,
    orderItemHasError: array.isRequired,
};

const mapStateToProps = (state) => {
    if (!state?.checkout) {
        return ({
            orderId: '',
            orderItemHasError: '',
            cartItems: '',
            continueToPayment: '',
            continueToReview: '',
            featureFlags: {},
        });
    }

    return ({
        orderId: commonSelectors.getOrderId(state),
        orderItemHasError: commonSelectors.getOrderItemsWithError(state),
        cartItems: commonSelectors.getRecipientsExcludePassport(state),
        continueToPayment: commonSelectors.continueToPayment(state),
        continueToReview: commonSelectors.getReviewOrderStatus(state),
        featureFlags: getFeatureFlags(state),
    });
};

const mapDispatchToProps = (dispatch) => ({
    triggerValidateOrder: bindActionCreators(recipientActions.callToTriggerValidateOrder, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(Breadcrumb));
